import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { warehousesService, Warehouse, WarehouseCreateEntry } from '../services/warehousesService';

// shadcn/ui components
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { AlertTriangle } from 'lucide-react';

interface WarehouseFormProps {
  open: boolean;
  onClose: () => void;
  onSave: (warehouse: Warehouse) => void;
  warehouse?: Warehouse | null;
}

export const ModernWarehouseForm: React.FC<WarehouseFormProps> = ({
  open,
  onClose,
  onSave,
  warehouse,
}) => {
  const { translate } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<WarehouseCreateEntry>({
    name: '',
    description: '',
    address: '',
    additionalAddressInfo: '',
    contact: '',
    location: {
      latitude: 0,
      longitude: 0,
    },
  });

  useEffect(() => {
    if (open) {
      if (warehouse) {
        setFormData({
          name: warehouse.name,
          description: warehouse.description,
          address: warehouse.address,
          additionalAddressInfo: warehouse.additionalAddressInfo || '',
          contact: warehouse.contact,
          location: warehouse.location,
        });
      } else {
        setFormData({
          name: '',
          description: '',
          address: '',
          additionalAddressInfo: '',
          contact: '',
          location: {
            latitude: 0,
            longitude: 0,
          },
        });
      }
      setError(null);
    }
  }, [open, warehouse]);

  const handleInputChange = (
    field: keyof WarehouseCreateEntry,
    value: any
  ) => {
    if (field === 'location') {
      setFormData((prev) => ({
        ...prev,
        location: value,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate required fields
      if (!formData.name || !formData.description || !formData.address || !formData.contact) {
        setError(translate('pos.warehouses.form.required', 'Please fill in all required fields'));
        setLoading(false);
        return;
      }

      if (!formData.location.latitude || !formData.location.longitude) {
        setError(translate('pos.warehouses.form.locationRequired', 'Please provide location coordinates'));
        setLoading(false);
        return;
      }

      let savedWarehouse: Warehouse;
      if (warehouse) {
        savedWarehouse = await warehousesService.updateWarehouse(
          warehouse.id,
          formData
        );
      } else {
        savedWarehouse = await warehousesService.createWarehouse(formData);
      }

      onSave(savedWarehouse);
    } catch (err) {
      console.error('Failed to save warehouse:', err);
      setError(
        err instanceof Error
          ? err.message
          : translate('pos.warehouses.form.error', 'Failed to save warehouse')
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {warehouse
              ? translate('pos.warehouses.edit', 'Edit Warehouse')
              : translate('pos.warehouses.create', 'Create Warehouse')}
          </DialogTitle>
          <DialogDescription>
            {warehouse
              ? translate('pos.warehouses.form.editDescription', 'Update warehouse information')
              : translate('pos.warehouses.form.createDescription', 'Create a new warehouse')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Card>
            <CardHeader>
              <CardTitle>
                {translate('pos.warehouses.form.basicInfo', 'Basic Information')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">
                  {translate('pos.warehouses.form.name', 'Name')} *
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">
                  {translate('pos.warehouses.form.description', 'Description')} *
                </Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">
                  {translate('pos.warehouses.form.address', 'Address')} *
                </Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="additionalAddressInfo">
                  {translate('pos.warehouses.form.additionalAddressInfo', 'Additional Address Info')}
                </Label>
                <Input
                  id="additionalAddressInfo"
                  value={formData.additionalAddressInfo || ''}
                  onChange={(e) => handleInputChange('additionalAddressInfo', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact">
                  {translate('pos.warehouses.form.contact', 'Contact')} *
                </Label>
                <Input
                  id="contact"
                  value={formData.contact}
                  onChange={(e) => handleInputChange('contact', e.target.value)}
                  required
                />
              </div>
            </CardContent>
          </Card>

          <Card className="mt-4">
            <CardHeader>
              <CardTitle>
                {translate('pos.warehouses.form.location', 'Location')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="latitude">
                    {translate('pos.warehouses.form.latitude', 'Latitude')} *
                  </Label>
                  <Input
                    id="latitude"
                    type="number"
                    step="any"
                    value={formData.location.latitude}
                    onChange={(e) =>
                      handleInputChange('location', {
                        ...formData.location,
                        latitude: parseFloat(e.target.value) || 0,
                      })
                    }
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="longitude">
                    {translate('pos.warehouses.form.longitude', 'Longitude')} *
                  </Label>
                  <Input
                    id="longitude"
                    type="number"
                    step="any"
                    value={formData.location.longitude}
                    onChange={(e) =>
                      handleInputChange('location', {
                        ...formData.location,
                        longitude: parseFloat(e.target.value) || 0,
                      })
                    }
                    required
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={onClose}>
              {translate('common.cancel', 'Cancel')}
            </Button>
            <Button type="submit" disabled={loading}>
              {loading
                ? translate('common.saving', 'Saving...')
                : translate('common.save', 'Save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

